import React, { useEffect, useState } from "react";
import { Play, ArrowUpRight } from "lucide-react";
import useReveal from "@/hooks/useReveal";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";

/**
 * VideoDestaque, um único vídeo em destaque (não é a grade completa).
 * Consome /api/videos e pega o primeiro item. Silencia se lista vazia.
 * CTA "Assistir" abre a URL de embed em nova aba.
 */
export default function VideoDestaque() {
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const r = useReveal();

  useEffect(() => {
    api
      .get("/videos")
      .then(({ data }) => {
        if (Array.isArray(data) && data.length) setVideo(data[0]);
      })
      .catch(() => setVideo(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !video) return null;

  // Converte embed URL do Instagram para link direto assistível
  const watchUrl = (video.video_url || "").replace(/\/embed\/?$/, "/");

  return (
    <section
      id="video-destaque"
      data-testid="video-destaque-section"
      className="relative bg-[#F7F5F0] py-16 md:py-20 border-t border-[#0B1E36]/6"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-8">
        <div ref={r} className="reveal grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-8 bg-[#C5A059]" aria-hidden />
              <span className="text-[0.68rem] uppercase tracking-[0.32em] text-[#C5A059] font-medium">
                Vídeo em destaque
              </span>
            </div>
            <h2
              data-testid="video-destaque-title"
              className="font-serif-display text-[#0B1E36] text-[1.85rem] md:text-[2.35rem] lg:text-[2.75rem] leading-[1.1] tracking-[-0.02em] font-medium"
            >
              {video.title}
            </h2>
            {video.impact_phrase && (
              <p className="mt-5 font-serif-display italic text-[1.1rem] md:text-[1.2rem] text-[#0B1E36]/80 leading-[1.6]">
                &ldquo;{video.impact_phrase}&rdquo;
              </p>
            )}
            {video.cta && (
              <p className="mt-4 text-[0.95rem] text-[#1F1A17]/65 leading-[1.7] font-light">
                {video.cta}
              </p>
            )}
            <a
              href={watchUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="video-destaque-cta"
              onClick={() =>
                track("video_open", {
                  id: video.id,
                  source: "home_video_destaque",
                })
              }
              className="mt-8 inline-flex items-center gap-3 bg-[#0B1E36] hover:bg-[#0B2545] text-white px-7 py-3.5 text-[0.72rem] uppercase tracking-[0.28em] font-medium transition-colors"
            >
              <Play size={13} strokeWidth={1.8} fill="currentColor" />
              Assistir agora
              <ArrowUpRight size={13} strokeWidth={1.6} />
            </a>
          </div>

          <div className="md:col-span-7">
            <div
              data-testid="video-destaque-embed"
              className="relative w-full bg-black overflow-hidden border border-[#0B1E36]/10"
              style={{ paddingTop: "56.25%" }}
            >
              {video.video_url ? (
                <iframe
                  src={video.video_url}
                  title={video.title}
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-white/50 text-sm">
                  Vídeo indisponível
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
